import { useEffect, useState } from "react";
import { config } from "../../../utils/config";
import axios from "axios";
import { findDataInFields, waitForCookie } from "../../../utils/helperFuctions";
import { getCookie, setCookie } from "../../../utils/cookieHandler";
import PaginationButtons from "../../DynamicList/PaginationButtons";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { PropagateLoader } from "react-spinners";

const VehiclesDisplay = ({
  label,
  name,
  id,
  placeholder,
  defaultValue,
  err,
  handleChange,
  url,
  returnParameter,
  toShow,
  data,
  isSearchable,
  statusColors,
  searchPlaceholder,
  filters,
  dependson,
  required,
  fields,
  disabled,
  disabledIfField,
  removeValueOnWhichFieldValueChange,
  dataParam,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const q = searchParams.get("q");
  const category = searchParams.get("category");
  const sortBy = searchParams.get("sortBy");
  const sDirection = searchParams.get("sortDirection");

  const selectedCategory = findDataInFields(
    fields,
    "vehicle_category_display.class"
  );
  const [selectedCategoryState, setSelectedCategoryState] = useState(() => {
    if (category != null) return `${category}`;
    if (selectedCategory?.id != null) return `${selectedCategory.id}`;
    return "";
  });

  const [sortDirection, setSortDirection] = useState(sDirection || "asc");
  const [searchTerm, setSearchTerm] = useState(q || "");
  const [page, setPage] = useState(currentPage);
  const [totalCount, setTotalCount] = useState(null);
  const [allCategories, setAllCategories] = useState(null);
  const [sortedField, setSortedField] = useState(sortBy || null);

  const [loading, setLoading] = useState(false);
  const [vehiclesData, setVehiclesData] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(
    defaultValue?.id ?? null
  );

  const addFilters = (filters) => {
    if (!filters) return "";
    let concatFilters = "";
    for (const key in filters) {
      if (Object.hasOwnProperty.call(filters, key)) {
        const element = filters[key];
        if (element.includes("<s>")) {
          concatFilters += `${key}=${element.replace("<s>", "")}&`;
        } else if (element.includes("<now>")) {
          concatFilters += `${key}=${new Date().toISOString()}&`;
        } else {
          concatFilters += `${key}=${encodeURIComponent(
            findDataInFields(fields, element)
          )}&`;
        }
      }
    }
    return concatFilters;
  };

  const allFilters = addFilters(filters);

  let cancelTokenSource;

  const fetchVehicleData = async ({
    page = 1,
    search = searchTerm,
    sortField = sortedField,
    sortOrder = sortDirection,
    categoryId = selectedCategoryState,
  } = {}) => {
    setLoading(true);

    if (cancelTokenSource)
      cancelTokenSource.cancel("Canceled previous request");
    cancelTokenSource = axios.CancelToken.source();

    const axiosConfig = {
      cancelToken: cancelTokenSource.token,
      headers: { Authorization: `Bearer ${getCookie("_AT")}` },
    };

    try {
      const _r = await waitForCookie("_r");
      let urlQuery = `${config.apiUrl}/api/${url}?currentRole=${_r}&page=${page}`;
      if (search) urlQuery += `&q=${search}`;
      if (sortField)
        urlQuery += `&sortBy=${sortField}&sortDirection=${sortOrder}`;
      if (categoryId) urlQuery += `&category=${categoryId}`;
      if (allFilters) urlQuery += `&${allFilters}`;

      const resp = await axios.get(urlQuery, axiosConfig);
      if (resp.headers?.authorization) {
        setCookie("_AT", resp.headers.authorization.split(" ")[1]);
      }

      const respData = dataParam ? resp.data.data[dataParam] : resp.data;
      if (selectedVehicleId) {
        const selectedVehicleResp = await axios.get(
          `${config.apiUrl}/api/${url}/${selectedVehicleId}`,
          axiosConfig
        );
        setVehiclesData([
          selectedVehicleResp.data,
          ...respData.data.filter((v) => v.id !== selectedVehicleResp.data.id),
        ]);
      } else {
        setVehiclesData(respData.data);
      }

      setTotalCount(respData.totalCount);
      setPage(page);

      const branch = findDataInFields(fields, "branch.id");
      const classResp = await axios.get(
        `${config.apiUrl}/api/${url}/getclasses?branch=${branch}`,
        axiosConfig
      );
      setAllCategories(classResp.data);
    } catch (err) {
      console.error("Fetch error", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleData();
  }, []);

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicleId(vehicle.id);
    handleChange({
      target: {
        name: name,
        value: {
          id: vehicle.id,
          class: vehicle.class.id,
        },
      },
    });
  };

  const filteredVehicles = vehiclesData;

  return (
    <div className="sm:col-span-4 md:col-span-6 relative">
      <div className="mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex justify-between flex-1 min-w-[220px] max-h-[38px] w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            <input
              type="text"
              placeholder="Search vehicles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-md flex-1 px-2"
              style={{ outline: "none", border: "none" }}
            />
            <button
              type="button"
              className="mr-2"
              onClick={() => fetchVehicleData({ search: searchTerm })}
            >
              <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="min-w-[180px]">
            <select
              onChange={(e) => {
                const direction = e.target.value;
                setSortDirection(direction);
                setSortedField("brand.name");
                fetchVehicleData({
                  sortOrder: direction,
                  sortField: "brand.name",
                });
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              defaultValue=""
            >
              <option value="" disabled>
                Sort by
              </option>
              <option value="asc">Car Name (A to Z)</option>
              <option value="desc">Car Name (Z to A)</option>
            </select>
          </div>

          <div className="min-w-[180px]">
            <select
              value={
                allCategories?.some(
                  (cat) => String(cat.id) === selectedCategoryState
                )
                  ? selectedCategoryState
                  : ""
              }
              onChange={(e) => {
                setSelectedCategoryState(e.target.value);
                fetchVehicleData({ categoryId: e.target.value });
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="" disabled>
                All Categories
              </option>
              {allCategories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setSortDirection("asc");
              setSortedField(null);
              setSelectedCategoryState("");
              fetchVehicleData({ search: "", sortField: null, categoryId: "" });
              setSearchParams({ page: 1 });
            }}
            className="border border-primary text-primary bg-white px-3 py-2 rounded-md text-sm hover:bg-blue-50 transition"
          >
            Reset filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center pt-12" style={{ height: "50vh" }}>
          <PropagateLoader color="#0167ff" />
        </div>
      ) : filteredVehicles?.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filteredVehicles.map((vehicle) => {
            const isSelected = vehicle.id === selectedVehicleId;
            return (
              <div
                key={vehicle.id}
                onClick={() => handleSelectVehicle(vehicle)}
                className={`cursor-pointer bg-white rounded-2xl overflow-hidden  border transition-all duration-200 ${
                  isSelected
                    ? "border-primary ring-2 ring-primary "
                    : "border-gray-200 "
                }`}
              >
                <div className="relative w-full pb-[56.25%] bg-gray-100 overflow-hidden">
                  {vehicle.image ? (
                    <img
                      src={`${config.apiUrl}/assets/${vehicle.image.name}`}
                      alt={`${vehicle.brand.name} ${vehicle.model.name}`}
                      className="absolute top-0 left-0 w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No Image
                    </div>
                  )}
                </div>

                <div className="p-4 text-sm text-gray-700 space-y-1">
                  <div className=" text-black text-[16px] font-bold mb-2">
                    {vehicle.brand.name} {vehicle.model.name} - {vehicle.color?.name}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Plate: </span>{" "}
                    {vehicle.plate}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Year: </span>{" "}
                    {vehicle.year}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">
                      Category:{" "}
                    </span>{" "}
                    {vehicle.class.name}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">
                      Location:{" "}
                    </span>{" "}
                    {vehicle.location?.name || "N/A"}
                  </div>
                  <div className="mt-2" style={{ marginTop: "12px" }}>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        vehicle.status === "Available"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {vehicle.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="flex justify-center items-center"
          style={{ height: "50vh" }}
        >
          No vehicles found
        </div>
      )}

      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-gray-200 mt-4 pt-0 pb-2 z-10">
        <PaginationButtons
          page={page}
          totalCount={totalCount}
          nextButtonOnClick={() => fetchVehicleData({ page: page + 1 })}
          backButtonOnClick={() => fetchVehicleData({ page: page - 1 })}
        />
      </div>
    </div>
  );
};

export default VehiclesDisplay;
